import React, { useState, useEffect } from "react";
import { Container, Row, Col, Spinner, Modal, Button } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaInfo,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import { MdTempleBuddhist } from "react-icons/md";
import { buddhismAPI } from "../utils/api";

const PrayList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [buddhismList, setBuddhismList] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [region, setRegion] = useState(searchParams.get("region") || "전체");

  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get("page")) || 1
  );
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(9);

  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState("");

  const regions = [
    "전체","서울","경기","인천","강원","충북","충남",
    "대전","세종","경북","경남","대구","울산","부산",
    "전북","전남","광주","제주",
  ];

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    setIsAuthenticated(!!token);

    const pageParam = searchParams.get("page");
    const searchParam = searchParams.get("search");
    const regionParam = searchParams.get("region");

    if (pageParam) setCurrentPage(parseInt(pageParam));
    if (searchParam) setSearch(searchParam);
    if (regionParam) setRegion(regionParam);

    fetchBuddhismList(
      parseInt(pageParam) || 1,
      searchParam || "",
      regionParam || "전체"
    );
  }, [searchParams]);

  const fetchBuddhismList = async (page, searchText, regionFilter) => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (searchText) params.search = searchText;
      if (regionFilter && regionFilter !== "전체") params.region = regionFilter;
      params.page = page;
      params.size = itemsPerPage;

      const response = await buddhismAPI.getAll(params);
      setBuddhismList(response.data.results || []);

      const totalItems = response.data.count || 0;
      setTotalPages(Math.ceil(totalItems / itemsPerPage) || 1);
    } catch (err) {
      if (err.response?.status === 401 && isAuthenticated) {
        setError("세션이 만료되었습니다. 다시 로그인해 주세요.");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        setIsAuthenticated(false);
      } else {
        setError("사찰 정보를 불러오는 중 오류가 발생했습니다.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateSearchParams(1, search, region);
  };

  const handleReset = () => {
    setSearch("");
    setRegion("전체");
    updateSearchParams(1, "", "전체");
  };

  const handlePageChange = (pageNumber) => {
    updateSearchParams(pageNumber, search, region);
  };

  const updateSearchParams = (page, searchText, regionFilter) => {
    const params = new URLSearchParams();
    if (page > 1) params.append("page", page);
    if (searchText) params.append("search", searchText);
    if (regionFilter && regionFilter !== "전체") params.append("region", regionFilter);
    setSearchParams(params);
  };

  const handleShowModal = (title, content) => {
    setModalTitle(title);
    setModalContent(content);
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  // 페이지 번호 배열 계산
  const getPageNums = () => {
    const pages = [];
    const total = Math.min(5, totalPages);
    let start = 1;
    if (totalPages > 5) {
      if (currentPage <= 3) start = 1;
      else if (currentPage >= totalPages - 2) start = totalPages - 4;
      else start = currentPage - 2;
    }
    for (let i = 0; i < total; i++) pages.push(start + i);
    return pages;
  };

  return (
    <div className="pl-wrap">
      {/* ── 헤더 ── */}
      <section className="pl-hero">
        <div className="pl-hero-dharma" aria-hidden="true">☸</div>
        <Container className="pl-hero-inner">
          <span className="pl-badge">공양 사찰</span>
          <h1 className="pl-hero-title">사찰 목록</h1>
          <p className="pl-hero-sub">
            다양한 사찰 정보를 찾아보고 공양을 올려보세요.<br />
            공양을 통해 마음의 평화와 공덕을 쌓을 수 있습니다.
          </p>
        </Container>
      </section>

      <Container className="pl-container">

        {/* ── 검색 박스 ── */}
        <div className="pl-search-box">
          <form onSubmit={handleSearch} className="pl-search-form">
            {/* 검색 인풋 */}
            <div className="pl-search-field">
              <FaSearch className="pl-search-ico" />
              <input
                type="text"
                className="pl-input"
                placeholder="사찰 이름으로 검색"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* 지역 셀렉트 */}
            <div className="pl-select-field">
              <select
                className="pl-select"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
              >
                {regions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            {/* 버튼들 */}
            <button type="submit" className="pl-btn pl-btn--primary">검색</button>
            <button type="button" className="pl-btn pl-btn--reset" onClick={handleReset}>
              <FaTimes />
            </button>
          </form>
        </div>

        {/* ── 지역 필터 칩 ── */}
        <div className="pl-region-chips">
          {regions.map((r) => (
            <button
              key={r}
              className={`pl-chip${r === region ? " pl-chip--active" : ""}`}
              onClick={() => {
                setRegion(r);
                updateSearchParams(1, search, r);
              }}
            >
              {r}
            </button>
          ))}
        </div>

        {/* ── 컨텐츠 ── */}
        {loading ? (
          <div className="pl-loading">
            <Spinner animation="border" style={{ color: "var(--main-color)", width: "2.5rem", height: "2.5rem" }} />
            <p>사찰 정보를 불러오는 중입니다…</p>
          </div>
        ) : error ? (
          <div className="pl-error-box">
            <span className="pl-error-ico">⚠</span>
            <p>{error}</p>
            <button
              className="pl-btn pl-btn--outline"
              onClick={() => fetchBuddhismList(currentPage, search, region)}
            >
              다시 시도하기
            </button>
          </div>
        ) : buddhismList.length === 0 ? (
          <div className="pl-empty-box">
            <span className="pl-empty-dharma">☸</span>
            <h3>검색 결과가 없습니다</h3>
            <p>다른 검색어나 필터를 사용해 보세요.</p>
            <button className="pl-btn pl-btn--outline" onClick={handleReset}>
              전체 목록 보기
            </button>
          </div>
        ) : (
          <>
            {/* 결과 요약 */}
            <div className="pl-summary">
              <span>
                총 <strong>{buddhismList.length}</strong>개의 사찰
                {region !== "전체" && <em className="pl-summary-tag">{region}</em>}
                {search && <em className="pl-summary-tag">{search}</em>}
              </span>
              <span className="pl-summary-page">
                {(currentPage - 1) * itemsPerPage + 1}–
                {Math.min(currentPage * itemsPerPage, (currentPage - 1) * itemsPerPage + buddhismList.length)} 표시
              </span>
            </div>

            {/* 카드 그리드 */}
            <Row xs={1} md={2} lg={3} className="g-4 mb-5">
              {buddhismList.map((temple) => (
                <Col key={temple.id}>
                  <div
                    className={`pl-card ${temple.is_preparing ? 'pl-card--preparing' : ''}`}
                    onClick={() => navigate(`/buddhism/${temple.id}`, { state: { temple } })}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && navigate(`/buddhism/${temple.id}`, { state: { temple } })}
                  >
                    {/* 이미지 */}
                    <div className="pl-card-img-wrap">
                      {temple.images && temple.images.length > 0 ? (
                        <img
                          src={temple.images[0].url || temple.images[0]}
                          alt={temple.name}
                          className="pl-card-img"
                        />
                      ) : (
                        <div className="pl-card-img-placeholder">
                          <MdTempleBuddhist />
                        </div>
                      )}
                      {temple.region && (
                        <span className="pl-card-region">{temple.region}</span>
                      )}
                      {temple.is_preparing && (
                        <span className="pl-card-preparing">준비중</span>
                      )}
                    </div>

                    {/* 본문 */}
                    <div className="pl-card-body">
                      {temple.type && (
                        <span className="pl-card-type">{temple.type}</span>
                      )}
                      <h3 className="pl-card-name">{temple.name}</h3>

                      {temple.chief_monk && (
                        <p
                          className="pl-card-monk"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowModal("사찰 소개", temple.chief_monk);
                          }}
                        >
                          <FaInfo className="pl-card-ico" />
                          {temple.chief_monk.length > 28
                            ? `${temple.chief_monk.substring(0, 28)}…`
                            : temple.chief_monk}
                        </p>
                      )}

                      {temple.address && (
                        <p
                          className="pl-card-addr"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowModal("주소 정보", temple.address);
                          }}
                        >
                          <FaMapMarkerAlt className="pl-card-ico" />
                          {temple.address}
                        </p>
                      )}

                      <div className="pl-card-footer">
                        {temple.is_preparing ? (
                          <span className="pl-card-link-disabled" onClick={e => e.stopPropagation()}>
                            공양 준비중입니다
                          </span>
                        ) : (
                          <span
                            className="pl-card-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/buddhism/${temple.id}`, { state: { temple } });
                            }}
                          >
                            공양 신청 →
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="pl-pagination">
                <button
                  className="pl-page-btn"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >«</button>
                <button
                  className="pl-page-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >‹</button>

                {getPageNums().map((n) => (
                  <button
                    key={n}
                    className={`pl-page-btn${n === currentPage ? " pl-page-btn--active" : ""}`}
                    onClick={() => handlePageChange(n)}
                  >{n}</button>
                ))}

                <button
                  className="pl-page-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >›</button>
                <button
                  className="pl-page-btn"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >»</button>
              </div>
            )}
          </>
        )}
      </Container>

      {/* ── 모달 ── */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton style={{ border: "none", padding: "1.5rem 1.5rem 0.5rem" }}>
          <Modal.Title style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--main-color)" }}>
            {modalTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "1rem 1.5rem", lineHeight: 1.8, color: "#444" }}>
          {modalContent}
        </Modal.Body>
        <Modal.Footer style={{ border: "none", padding: "0.5rem 1.5rem 1.5rem" }}>
          <button className="pl-btn pl-btn--primary" onClick={handleCloseModal}>닫기</button>
        </Modal.Footer>
      </Modal>

      {/* ── 스타일 ── */}
      <style jsx="true">{`
        :root {
          --pl-teal: #009590;
          --pl-teal-dark: #007873;
          --pl-teal-pale: #e8f5f5;
          --pl-text: #2c3e35;
          --pl-muted: #6e8a84;
        }

        /* 히어로 */
        .pl-hero {
          position: relative;
          background: linear-gradient(160deg, #f0fafa 0%, #e4f5f4 60%, #d5efee 100%);
          padding: 72px 0 64px;
          text-align: center;
          overflow: hidden;
        }
        .pl-hero-dharma {
          position: absolute;
          font-size: 18rem;
          color: var(--pl-teal);
          opacity: 0.04;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
          line-height: 1;
        }
        .pl-hero-inner { position: relative; z-index: 2; }
        .pl-badge {
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          color: var(--pl-teal);
          text-transform: uppercase;
          margin-bottom: 12px;
          padding: 5px 14px;
          border: 1px solid var(--pl-teal);
          border-radius: 2px;
        }
        .pl-hero-title {
          font-size: 2.8rem;
          font-weight: 700;
          color: var(--pl-teal);
          letter-spacing: -0.02em;
          margin: 10px 0 16px;
          line-height: 1.15;
        }
        .pl-hero-sub {
          font-size: 1rem;
          color: var(--pl-muted);
          line-height: 1.9;
          margin: 0;
        }

        /* 컨테이너 */
        .pl-container {
          padding-top: 40px;
          padding-bottom: 60px;
        }

        /* 검색 박스 */
        .pl-search-box {
          background: #fff;
          border: 1px solid rgba(0,149,144,0.15);
          border-radius: 4px;
          padding: 24px 28px;
          margin-bottom: 20px;
          box-shadow: 0 4px 18px rgba(0,149,144,0.07);
        }
        .pl-search-form {
          display: flex;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
        }
        .pl-search-field {
          flex: 1 1 260px;
          min-width: 200px;
          position: relative;
        }
        .pl-search-ico {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--pl-muted);
          font-size: 0.85rem;
        }
        .pl-input {
          width: 100%;
          border: 1px solid #dde8e7;
          border-radius: 3px;
          padding: 11px 14px 11px 38px;
          font-size: 0.92rem;
          color: var(--pl-text);
          outline: none;
          transition: border-color 0.2s;
          font-family: 'Noto Sans KR', sans-serif;
        }
        .pl-input:focus { border-color: var(--pl-teal); }
        .pl-select-field { flex: 0 0 140px; }
        .pl-select {
          width: 100%;
          border: 1px solid #dde8e7;
          border-radius: 3px;
          padding: 11px 14px;
          font-size: 0.92rem;
          color: var(--pl-text);
          background: #fff;
          outline: none;
          cursor: pointer;
          font-family: 'Noto Sans KR', sans-serif;
          transition: border-color 0.2s;
        }
        .pl-select:focus { border-color: var(--pl-teal); }

        /* 버튼 */
        .pl-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 10px 24px;
          border-radius: 3px;
          font-size: 0.88rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          cursor: pointer;
          border: none;
          font-family: 'Noto Sans KR', sans-serif;
          transition: all 0.25s ease;
        }
        .pl-btn--primary {
          background: var(--pl-teal);
          color: #fff;
        }
        .pl-btn--primary:hover {
          background: var(--pl-teal-dark);
          transform: translateY(-1px);
        }
        .pl-btn--outline {
          background: transparent;
          color: var(--pl-teal);
          border: 1.5px solid var(--pl-teal);
        }
        .pl-btn--outline:hover {
          background: var(--pl-teal);
          color: #fff;
        }
        .pl-btn--reset {
          background: #f3f4f6;
          color: #888;
          padding: 10px 14px;
        }
        .pl-btn--reset:hover { background: #e8e9ec; color: #555; }

        /* 지역 칩 */
        .pl-region-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 32px;
        }
        .pl-chip {
          padding: 7px 16px;
          border-radius: 2px;
          border: 1px solid #dde8e7;
          background: #fff;
          font-size: 0.82rem;
          color: var(--pl-muted);
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: 'Noto Sans KR', sans-serif;
        }
        .pl-chip:hover { border-color: var(--pl-teal); color: var(--pl-teal); }
        .pl-chip--active {
          background: var(--pl-teal);
          border-color: var(--pl-teal);
          color: #fff;
          font-weight: 600;
          box-shadow: 0 3px 10px rgba(0,149,144,0.22);
        }

        /* 로딩 */
        .pl-loading {
          text-align: center;
          padding: 80px 0;
          color: var(--pl-muted);
        }
        .pl-loading p { margin-top: 18px; font-size: 0.92rem; }

        /* 에러 */
        .pl-error-box {
          text-align: center;
          padding: 60px 20px;
          border: 1px solid #f5c6cb;
          border-radius: 4px;
          background: #fff8f8;
        }
        .pl-error-ico { font-size: 2rem; color: #e74c3c; display: block; margin-bottom: 12px; }
        .pl-error-box p { color: #666; margin-bottom: 20px; }

        /* 빈 결과 */
        .pl-empty-box {
          text-align: center;
          padding: 80px 20px;
        }
        .pl-empty-dharma {
          font-size: 4rem;
          color: var(--pl-teal);
          opacity: 0.2;
          display: block;
          margin-bottom: 16px;
        }
        .pl-empty-box h3 { font-size: 1.2rem; color: var(--pl-text); margin-bottom: 8px; }
        .pl-empty-box p { color: var(--pl-muted); font-size: 0.9rem; margin-bottom: 20px; }

        /* 결과 요약 */
        .pl-summary {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.88rem;
          color: var(--pl-muted);
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 8px;
        }
        .pl-summary strong { color: var(--pl-teal); font-weight: 700; }
        .pl-summary-tag {
          display: inline-block;
          font-style: normal;
          background: var(--pl-teal-pale);
          color: var(--pl-teal);
          font-size: 0.76rem;
          font-weight: 600;
          padding: 2px 10px;
          border-radius: 2px;
          margin-left: 6px;
        }
        .pl-summary-page { font-size: 0.82rem; }

        /* 카드 */
        .pl-card {
          background: #fff;
          border: 1px solid #e4edec;
          border-radius: 4px;
          overflow: hidden;
          cursor: pointer;
          height: 100%;
          display: flex;
          flex-direction: column;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .pl-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 16px 36px rgba(0,149,144,0.12);
        }

        .pl-card-img-wrap {
          position: relative;
          overflow: hidden;
          height: 210px;
        }
        .pl-card-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
          display: block;
        }
        .pl-card:hover .pl-card-img { transform: scale(1.06); }
        .pl-card-img-placeholder {
          width: 100%;
          height: 100%;
          background: var(--pl-teal-pale);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 3rem;
          color: var(--pl-teal);
          opacity: 0.4;
        }
        .pl-card-region {
          position: absolute;
          top: 13px; right: 13px;
          background: rgba(255,255,255,0.92);
          color: var(--pl-teal);
          font-size: 0.74rem;
          font-weight: 600;
          padding: 4px 11px;
          border-radius: 2px;
          letter-spacing: 0.06em;
          backdrop-filter: blur(4px);
        }
        .pl-card--preparing {
          opacity: 0.55;
          filter: grayscale(85%);
          background: #fafafa;
        }
        .pl-card--preparing:hover {
          opacity: 0.8;
          filter: grayscale(40%);
        }

        .pl-card-preparing {
          position: absolute;
          top: 13px; left: 13px;
          background: #d35400; /* 강조된 주황/붉은색 */
          color: #fff;
          font-size: 0.78rem;
          font-weight: 700;
          padding: 5px 12px;
          border-radius: 2px;
          letter-spacing: 0.08em;
          backdrop-filter: blur(4px);
          box-shadow: 0 2px 8px rgba(211,84,0,0.3);
          z-index: 10;
        }

        .pl-card-body {
          padding: 20px 22px 22px;
          display: flex;
          flex-direction: column;
          flex: 1;
        }
        .pl-card-type {
          display: inline-block;
          font-size: 0.69rem;
          font-weight: 600;
          letter-spacing: 0.1em;
          color: var(--pl-teal);
          border: 1px solid var(--pl-teal);
          padding: 2px 9px;
          border-radius: 2px;
          margin-bottom: 10px;
          opacity: 0.8;
        }
        .pl-card-name {
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--pl-text);
          margin-bottom: 10px;
          letter-spacing: -0.01em;
          line-height: 1.3;
        }
        .pl-card-monk,
        .pl-card-addr {
          font-size: 0.82rem;
          color: var(--pl-muted);
          display: flex;
          align-items: flex-start;
          gap: 6px;
          margin-bottom: 6px;
          cursor: pointer;
          line-height: 1.5;
        }
        .pl-card-monk:hover,
        .pl-card-addr:hover { color: var(--pl-teal); }
        .pl-card-ico {
          flex-shrink: 0;
          margin-top: 2px;
          color: var(--pl-teal);
          font-size: 0.8rem;
        }

        .pl-card-footer {
          margin-top: auto;
          padding-top: 16px;
          border-top: 1px solid #f0f5f5;
        }
        .pl-card-link {
          font-size: 0.84rem;
          font-weight: 600;
          color: var(--pl-teal);
          letter-spacing: 0.04em;
          transition: letter-spacing 0.25s ease;
          cursor: pointer;
        }
        .pl-card-link-disabled {
          font-size: 0.84rem;
          font-weight: 600;
          color: var(--pl-muted);
          letter-spacing: 0.04em;
          cursor: not-allowed;
        }
        .pl-card:hover .pl-card-link { letter-spacing: 0.1em; }

        /* 페이지네이션 */
        .pl-pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 6px;
          margin-top: 16px;
          margin-bottom: 20px;
        }
        .pl-page-btn {
          width: 38px;
          height: 38px;
          border-radius: 3px;
          border: 1px solid #dde8e7;
          background: #fff;
          color: var(--pl-muted);
          font-size: 0.9rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          font-family: 'Noto Sans KR', sans-serif;
        }
        .pl-page-btn:hover:not(:disabled) {
          border-color: var(--pl-teal);
          color: var(--pl-teal);
        }
        .pl-page-btn:disabled { opacity: 0.35; cursor: default; }
        .pl-page-btn--active {
          background: var(--pl-teal);
          border-color: var(--pl-teal);
          color: #fff;
          font-weight: 700;
        }

        /* 반응형 */
        @media (max-width: 768px) {
          .pl-hero { padding: 52px 0 44px; }
          .pl-hero-title { font-size: 2rem; }
          .pl-search-form { flex-direction: column; align-items: stretch; }
          .pl-select-field { flex: 1; }
          .pl-btn--primary, .pl-btn--reset { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default PrayList;